/* LAKSVEL — dagens velferdsregistrering, merd for merd.
 *
 * Laksvel er den standardiserte protokollen for operasjonell velferds-
 * overvåking av laks i matfiskanlegg (Havforskningsinstituttet, rapport
 * 2022-14, FHF 901554). Den fastsetter hva som observeres, hvor ofte, og på
 * hvilken skala — og hele poenget med en standard er at én merd kan
 * sammenliknes med en annen.
 *
 * HVORFOR DETTE ER ET RUTENETT OG IKKE EN VEIVISER
 *   Merdtelling gjør det samme valget og skriver ned begrunnelsen: en veiviser
 *   er for én telling tatt ved merdkanten med fisk i hånda, et rutenett er for
 *   å føre inn flere ringer tørt. Laksvel-tall skrives av fra et skjema eller
 *   fra en sonde, som regel for hele anlegget på én gang. Det er rutenettets
 *   tilfelle.
 *
 * HVORFOR GRUPPE OG MILJØ ER TO FLATER
 *   Fordi det er to jobber, og ofte to personer. Gruppeskåringen er en runde på
 *   gangbanen: tre trykk per merd, under et minutt for hele anlegget. Miljø er
 *   en sonde som senkes til fire dyp. Å tvinge dem inn i én tabell ville gjort
 *   den raske jobben treg, og det er den raske jobben som må gjøres hver dag.
 *
 * ALDRI ET SNITT
 *   §5.4: skåringsnivåene er ikke lineære, og resultater må «aldri presenteres
 *   som snitt-verdier, eller endringer i snitt-verdier». Hundre fisk på skår 1
 *   som blir nitti på 1 og ti på 3 flytter snittet fra 1,0 til 1,2 — som ser ut
 *   som ingenting — mens en tidel av populasjonen nettopp fikk alvorlig skade.
 *   Denne siden viser tall og farger, aldri et gjennomsnitt.
 *
 * DELVIS UTFYLT ER GYLDIG
 *   Et anlegg uten sonde skal fortsatt kunne føre atferd og appetitt. Ingenting
 *   her krever at noe annet er fylt ut først, og en tom celle tegnes som tom og
 *   ikke som null.
 */

import { useEffect, useState } from 'react'

import {
  laksvelPath, saveLaksvel,
  type LaksvelDay, type LaksvelPen, type LaksvelScale,
} from '../api'
import { Skala, type Band } from '../components/Skala'
import { Velferdsikon, type Ikon } from '../components/Velferdsikon'
import { ErrorBox, Rows } from '../components/bits'
import { PageHead } from '../components/Shell'
import { useApi } from '../useApi'

/** Dyp protokollen navngir (NS 9417). Det fjerde er merdas eget maksdyp, som
 *  hentes per ring — en verdi som står i tabellen som «maks» ville sluttet å
 *  bety noe den dagen nota ble hengt grunnere. */
const DYP = [3, 5, 15]

type Parameter = 'o2' | 'temp' | 'salt'

/** Protokollens egne ord for de fire nivåene (§3): «grønn indikerer innenfor
 *  optimalområde, gul utkant av optimalområde, orange utenfor, og rød langt
 *  utenfor optimalområde». Sitert framfor omskrevet — en omskrivning av et
 *  forbehold har det med å miste forbeholdet. */
const TEGN: { score: number; ord: string }[] = [
  { score: 0, ord: 'innenfor optimalområdet' },
  { score: 1, ord: 'utkanten av det' },
  { score: 2, ord: 'utenfor' },
  { score: 3, ord: 'langt utenfor' },
]

const PARAMETER: Record<Parameter, { navn: string; enhet: string }> = {
  o2: { navn: 'Oksygenmetning', enhet: '%' },
  temp: { navn: 'Temperatur', enhet: '°C' },
  salt: { navn: 'Saltholdighet', enhet: '‰' },
}

/** Norsk desimalkomma, fordi det er det både tastaturet og skjemaet gir.
 *  Tomt er null og ikke null-verdien: «ikke ført» og «målt til null» er to
 *  forskjellige påstander, og bare den ene av dem er en måling. */
function tall(s: string): number | null {
  const v = s.trim().replace(',', '.')
  if (v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function vis(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : String(n).replace('.', ',')
}

function idag(): string {
  return new Date().toISOString().slice(0, 10)
}

/** Endene av hver skala, som ikon og som ord.
 *
 *  Ordene ligger i `title` og ikke i teksten: en etikett under hver knapperad
 *  ville doblet høyden på en tabell som skal kunne skummes nedover, og ikonet
 *  gjør jobben på avstand. Ordene finnes for den som holder fingeren i ro, og
 *  for skjermleseren. */
const ENDER: Record<'atferd' | 'appetitt',
                    [{ ikon: Ikon; hva: string }, { ikon: Ikon; hva: string }]> = {
  atferd: [{ ikon: 'atferd-normal', hva: '0 — normal atferd' },
           { ikon: 'atferd-avvik', hva: '3 — svært tydelig avvikende atferd' }],
  appetitt: [{ ikon: 'appetitt-spiser', hva: '0 — spiser som forventet' },
             { ikon: 'appetitt-nei', hva: '3 — spiser svært lite' }],
}

/** Oksygenbåndene ved én temperatur.
 *
 *  Regnet ut her fra formelen tjenesten sender, og ikke slått opp i en tabell,
 *  fordi kantene IKKE er konstanter: grønt starter på 56 % ved 5 °C og på 94 %
 *  ved 18 °C. En tabell utlevert én gang ville vært feil ved alle temperaturer
 *  unntatt én. */
function oksygenBand(sk: LaksvelScale, temp: number | null): Band[] | null {
  if (temp === null) return null
  const { slope, intercept, yellow, green } = sk.o2.domaxfi
  const gulv = slope * temp + intercept
  const kanter = [sk.o2.min, gulv, gulv + yellow, gulv + green, sk.o2.max]
  const skaarer = [3, 2, 1, 0]
  const ut: Band[] = []
  skaarer.forEach((score, i) => {
    const a = Math.max(sk.o2.min, kanter[i]!)
    const b = Math.min(sk.o2.max, kanter[i + 1]!)
    if (b > a) ut.push({ from: a, to: b, score })
  })
  return ut
}

export function SiteLaksvel({ id }: { id: string }) {
  const [dato, setDato] = useState(idag())
  const [flate, setFlate] = useState<'gruppe' | 'miljo'>('gruppe')
  const [param, setParam] = useState<Parameter>('temp')
  const [valgtMerd, setValgtMerd] = useState<string | null>(null)

  const sted = useApi<{ name: string }>(`/v1/sites/${encodeURIComponent(id)}`)
  const dag = useApi<LaksvelDay>(laksvelPath(id, dato))

  /* Redigering holdes som tekst, tastet inn, og skrives ikke tilbake fra
     svaret mens noen skriver. Verdien på skjermen skal være den de akkurat
     tastet, ikke den serveren rakk å runde av. */
  const [utkast, setUtkast] = useState<Record<string, string>>({})
  const [lagrer, setLagrer] = useState<string | null>(null)
  const [feil, setFeil] = useState<string | null>(null)

  useEffect(() => { setUtkast({}) }, [dato])

  if (dag.cold || sted.cold) return <Rows n={6} />
  if (dag.error) return <ErrorBox error={dag.error} />

  const d = dag.data
  const pens = d?.pens ?? []

  const dypFor = (p: LaksvelPen) => [...DYP, p.depth_m ?? null]
  const merd = pens.find((p) => p.pen_id === valgtMerd) ?? pens[0] ?? null
  const nokkel = (penId: string, felt: string) => `${penId}:${felt}`

  const les = (penId: string, felt: string, lagret: number | null | undefined) => {
    const k = nokkel(penId, felt)
    return k in utkast ? utkast[k]! : vis(lagret)
  }
  const skriv = (penId: string, felt: string, v: string) =>
    setUtkast((u) => ({ ...u, [nokkel(penId, felt)]: v }))

  /** Lagre én merd. Én forespørsel per ring, som i Merdtelling og av samme
   *  grunn: et rutenett skal si fra om delvis suksess, ikke miste seks gode
   *  rader fordi den sjuende hadde en skrivefeil. */
  const lagre = async (p: LaksvelPen) => {
    setLagrer(p.pen_id)
    setFeil(null)
    try {
      const env = dypFor(p).map((dp, i) => {
        if (dp === null) return null
        const eksisterende = p.env.find((e) => e.depth_m === dp)
        return {
          depth_m: dp,
          o2_pct: tall(les(p.pen_id, `o2:${i}`, eksisterende?.o2_pct)),
          temp_c: tall(les(p.pen_id, `temp:${i}`, eksisterende?.temp_c)),
          salinity_ppt: tall(les(p.pen_id, `salt:${i}`, eksisterende?.salinity_ppt)),
          /* Alt som tastes her er tastet av et menneske. Det som er forhånds-
             utfylt beholder kilden sin fordi det ikke sendes på nytt med
             mindre noen har rørt det. */
          source: 'manual',
        }
      }).filter((e): e is NonNullable<typeof e> => e !== null)

      const got = await saveLaksvel(id, {
        pen_id: p.pen_id,
        on_date: dato,
        env,
        atferd: tall(les(p.pen_id, 'atferd', p.atferd)),
        appetitt: tall(les(p.pen_id, 'appetitt', p.appetitt)),
        n_dead: tall(les(p.pen_id, 'n_dead', p.n_dead)),
      })
      dag.patch(() => got)
      setUtkast((u) => {
        const ut = { ...u }
        for (const k of Object.keys(ut)) {
          if (k.startsWith(`${p.pen_id}:`)) delete ut[k]
        }
        return ut
      })
    } catch {
      setFeil(`Fikk ikke lagret merd ${p.pen_no}.`)
    } finally {
      setLagrer(null)
    }
  }

  const rort = (penId: string) =>
    Object.keys(utkast).some((k) => k.startsWith(`${penId}:`))

  return (
    <>
      <PageHead title={sted.data?.name ?? 'Laksvel'} tight />

      <div className="laksvel-topp">
        <label className="felt">
          <span>Dato</span>
          <input type="date" value={dato} max={idag()}
                 onChange={(e) => setDato(e.target.value)} />
        </label>
        <div className="laksvel-faner" role="tablist">
          <button type="button" role="tab" aria-selected={flate === 'gruppe'}
                  className={`btn sm${flate === 'gruppe' ? ' primary' : ' ghost'}`}
                  onClick={() => setFlate('gruppe')}>
            Gruppe
          </button>
          <button type="button" role="tab" aria-selected={flate === 'miljo'}
                  className={`btn sm${flate === 'miljo' ? ' primary' : ' ghost'}`}
                  onClick={() => setFlate('miljo')}>
            Miljø
          </button>
        </div>
      </div>

      {feil && <div className="notice bad">{feil}</div>}

      {pens.length === 0 && (
        <p className="tom">Ingen merder er lagt inn på dette anlegget ennå.</p>
      )}

      {flate === 'gruppe' && pens.length > 0 && (
        <div className="card stack">
          <p className="laksvel-hjelp">
            Én runde på gangbanen. Atferd og appetitt skåres 0–3 etter
            protokollen; 0 er normalt og 3 er tydelig avvik.
          </p>
          <div className="tabellrull">
            <table className="tbl laksveltabell">
              <thead>
                <tr>
                  <th>Merd</th>
                  <th>Atferd</th>
                  <th>Appetitt</th>
                  <th>Døde</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {pens.map((p) => (
                  <tr key={p.pen_id}>
                    <td data-l="Merd"><strong>{p.pen_no}</strong></td>
                    {(['atferd', 'appetitt'] as const).map((felt) => (
                      <td key={felt} data-l={felt === 'atferd' ? 'Atferd' : 'Appetitt'}>
                        {/* Et ikon i hver ende, ikke ett per trinn. Tallet er
                            skåren protokollen definerer og blir stående; det
                            ikonene legger til er hvilken VEI skalaen går, som
                            et tall alene ikke sier. */}
                        <div className="skaar">
                          <span className="skaar-ende" title={ENDER[felt][0].hva}>
                            <Velferdsikon ikon={ENDER[felt][0].ikon} />
                          </span>
                          {[0, 1, 2, 3].map((n) => {
                            const valgt = les(p.pen_id, felt, p[felt]) === String(n)
                            return (
                              <button key={n} type="button"
                                aria-pressed={valgt}
                                className={`skaarknapp${valgt ? ' er-valgt band-' + n : ''}`}
                                onClick={() => skriv(p.pen_id, felt,
                                                     valgt ? '' : String(n))}>
                                {n}
                              </button>
                            )
                          })}
                          <span className="skaar-ende" title={ENDER[felt][1].hva}>
                            <Velferdsikon ikon={ENDER[felt][1].ikon} />
                          </span>
                        </div>
                      </td>
                    ))}
                    <td data-l="Døde">
                      <input inputMode="numeric" className="tallfelt"
                             value={les(p.pen_id, 'n_dead', p.n_dead)}
                             onChange={(e) => skriv(p.pen_id, 'n_dead', e.target.value)} />
                    </td>
                    <td>
                      <button type="button" className="btn sm"
                              disabled={!rort(p.pen_id) || lagrer === p.pen_id}
                              onClick={() => lagre(p)}>
                        {lagrer === p.pen_id ? 'Lagrer…' : 'Lagre'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {flate === 'miljo' && pens.length > 0 && (
        <div className="card stack">
          {/* Én merd om gangen, og ett parameter om gangen — samme form som
              regnearket selv: en parameterblokk med dypene under hverandre.
              Sju merder ganger fire dyp er 28 skalaer, og de får ikke plass i
              et rutenett uten å bli for små til å treffe. */}
          <div className="laksvel-faner">
            {pens.map((p) => (
              <button key={p.pen_id} type="button"
                      className={`btn sm${valgtMerd === p.pen_id ? ' primary' : ' ghost'}`}
                      onClick={() => setValgtMerd(p.pen_id)}>
                {p.pen_no}
              </button>
            ))}
          </div>
          <div className="laksvel-faner">
            {(Object.keys(PARAMETER) as Parameter[]).map((k) => (
              <button key={k} type="button"
                      className={`btn sm${param === k ? ' primary' : ' ghost'}`}
                      onClick={() => setParam(k)}>
                {PARAMETER[k].navn}
              </button>
            ))}
          </div>

          {merd && (
            <>
              <p className="laksvel-hjelp">
                {PARAMETER[param].navn} i merd {merd.pen_no}, på 3, 5 og 15
                meter og på merdas maksdyp (NS 9417).
                {param === 'o2' && ' Grensene følger temperaturen, så de flytter'
                  + ' seg når du fører den — og de finnes ikke før den er ført.'}
                {param === 'temp' && ' Merda får fargen til det BESTE dypet:'
                  + ' fisk som kan svømme ned har allerede løst en varm overflate.'}
                {param === 'salt' && ' Saltholdighet har ingen fargeskala i'
                  + ' protokollen; den føres for å forstå sprangsjiktet.'}
              </p>

              {/* Tegnforklaringen én gang, ikke under hvert dyp. Saltholdighet
                  får ingen: protokollen skårer den ikke, og en fargeforklaring
                  til noe som ikke er farget ville påstått at den var det. */}
              {param !== 'salt' && (
                <ul className="skala-tegn">
                  {TEGN.map((t) => (
                    <li key={t.score}>
                      <i className={`band-${t.score}`} />
                      <span>{t.score} — {t.ord}</span>
                    </li>
                  ))}
                </ul>
              )}

              {dypFor(merd).map((dp, i) => {
                if (dp === null) {
                  return (
                    <p key={i} className="skala-sperret">
                      Maksdyp: merdas dyp er ikke ført på anlegget, så det
                      fjerde målepunktet finnes ikke å skrive i.
                    </p>
                  )
                }
                const e = merd.env.find((x) => x.depth_m === dp)
                const temp = tall(les(merd.pen_id, `temp:${i}`, e?.temp_c))
                const lagret = param === 'o2' ? e?.o2_pct
                  : param === 'temp' ? e?.temp_c : e?.salinity_ppt
                const naa = tall(les(merd.pen_id, `${param}:${i}`, lagret))
                const sk = d!.scale

                return (
                  <Skala key={i}
                    tittel={`${dp} m${dp === merd.depth_m ? ' (maks)' : ''}`}
                    enhet={PARAMETER[param].enhet}
                    verdi={naa}
                    onVerdi={(v) => skriv(merd.pen_id, `${param}:${i}`,
                                          v === null ? '' : vis(v))}
                    min={sk[param === 'o2' ? 'o2' : param === 'temp'
                          ? 'temp' : 'salinity'].min}
                    max={sk[param === 'o2' ? 'o2' : param === 'temp'
                          ? 'temp' : 'salinity'].max}
                    steg={sk[param === 'o2' ? 'o2' : param === 'temp'
                          ? 'temp' : 'salinity'].step}
                    bands={param === 'temp' ? sk.temp.bands
                      : param === 'o2' ? oksygenBand(sk, temp) : null}
                    merker={param === 'salt' ? sk.salinity.marks : undefined}
                    hint={e?.source === 'modelled' ? '~ fra varselet' : null}
                    sperret={param === 'o2' && temp === null
                      ? 'Før temperaturen på dette dypet først — grensen for '
                        + 'oksygen er en funksjon av den, så båndene finnes '
                        + 'ikke uten.'
                      : null} />
                )
              })}

              <div className="row">
                <button type="button" className="btn"
                        disabled={!rort(merd.pen_id) || lagrer === merd.pen_id}
                        onClick={() => lagre(merd)}>
                  {lagrer === merd.pen_id ? 'Lagrer…' : `Lagre merd ${merd.pen_no}`}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <p className="laksvel-kilde">
        Laksvel: standardisert operasjonell velferdsovervåking for laks i
        matfiskanlegg. Havforskningsinstituttet, rapport 2022-14 (FHF 901554).
        Individbaserte indikatorer føres på lusetellingen, der fisken uansett
        er tatt opp.
      </p>
    </>
  )
}
